import { useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";

const GRID = 100;
const SIZE = 70;

function WaveField() {
  const ref = useRef();
  const scratch = useMemo(() => ({
    cyan: new THREE.Color("#00F2FE"),
    green: new THREE.Color("#10E59A"),
    tmp: new THREE.Color(),
  }), []);

  const { positions, colors } = useMemo(() => {
    const count = GRID * GRID;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    let i = 0;
    for (let x = 0; x < GRID; x++) {
      for (let z = 0; z < GRID; z++) {
        positions[i * 3] = (x / (GRID - 1) - 0.5) * SIZE;
        positions[i * 3 + 1] = 0;
        positions[i * 3 + 2] = (z / (GRID - 1) - 0.5) * SIZE;
        i++;
      }
    }
    return { positions, colors };
  }, []);

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    const geo = ref.current.geometry;
    const pos = geo.attributes.position;
    const col = geo.attributes.color;
    const { cyan, green, tmp } = scratch;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const z = pos.getZ(i);
      const y =
        Math.sin(x * 0.32 + t * 0.7) * 0.7 +
        Math.cos(z * 0.28 + t * 0.55) * 0.7 +
        Math.sin((x + z) * 0.18 + t * 0.35) * 0.5;
      pos.setY(i, y);
      const mix = (Math.sin(x * 0.12 + t * 0.25) + 1) / 2;
      tmp.copy(cyan).lerp(green, mix).multiplyScalar(0.22 + ((y + 1.9) / 3.8) * 0.85);
      col.setXYZ(i, tmp.r, tmp.g, tmp.b);
    }
    pos.needsUpdate = true;
    col.needsUpdate = true;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
        <bufferAttribute attach="attributes-color" args={[colors, 3]} />
      </bufferGeometry>
      <pointsMaterial
        size={0.16}
        vertexColors
        transparent
        opacity={0.85}
        sizeAttenuation
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

const BUBBLE_COUNT = 260;

function Bubbles() {
  const ref = useRef();
  const { positions, speeds } = useMemo(() => {
    const positions = new Float32Array(BUBBLE_COUNT * 3);
    const speeds = new Float32Array(BUBBLE_COUNT);
    for (let i = 0; i < BUBBLE_COUNT; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 50;
      positions[i * 3 + 1] = Math.random() * 24 - 8;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 30;
      speeds[i] = 0.15 + Math.random() * 0.5;
    }
    return { positions, speeds };
  }, []);

  useFrame((_, delta) => {
    const pos = ref.current.geometry.attributes.position;
    for (let i = 0; i < BUBBLE_COUNT; i++) {
      let y = pos.getY(i) + speeds[i] * delta * 1.6;
      if (y > 14) y = -8;
      pos.setY(i, y);
    }
    pos.needsUpdate = true;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial
        size={0.14}
        color="#10E59A"
        transparent
        opacity={0.65}
        sizeAttenuation
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

export const OceanCanvas = () => {
  return (
    <div className="fixed inset-0 -z-10 pointer-events-none" data-testid="ocean-canvas">
      <Canvas camera={{ position: [0, 5, 13], fov: 60 }} dpr={[1, 1.5]}>
        <color attach="background" args={["#020813"]} />
        <fog attach="fog" args={["#020813", 18, 55]} />
        <group position={[0, -2, 0]} rotation={[-0.12, 0, 0]}>
          <WaveField />
        </group>
        <Bubbles />
      </Canvas>
      <div className="absolute inset-0 bg-gradient-to-b from-[#020813]/90 via-[#020813]/45 to-[#020813]/90" />
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 40%, rgba(2,8,19,0.55) 0%, rgba(2,8,19,0) 60%)" }} />
    </div>
  );
};
